import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Col, Container, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getLikesMe, getMyLikes, getViewedMe, getFavoritedMe, getMyFavorites } from '../../../Services/User/Likes';
import { getUserSubscriptionLogs } from '../../../Services/User/UserSubscriptionService';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import './Likes.css';
import { FaUser } from "react-icons/fa";

import useUserStore from '../../../Stores/UserStore';
import PremiumBadge from '../../../Components/PremiumBadge/PremiumBadge';

const Likes = () => {
  const pageTitle = useGoogleTranslate('Likes');
  const upgradeToUnlock = useGoogleTranslate('Upgrade to Unlock');
  const upgradeText = useGoogleTranslate('See who likes you and who viewed your profile with a Standard or Premium membership.');
  const viewPlans = useGoogleTranslate('View Plans');
  const loadingLabel = useGoogleTranslate('Loading...');
  const noUsersFound = useGoogleTranslate('No users found');
  const likesMeLabel = useGoogleTranslate('Likes Me');
  const myLikesLabel = useGoogleTranslate('My Likes');
  const viewedMeLabel = useGoogleTranslate('Viewed Me');
  const myFavoritesLabel = useGoogleTranslate('My Favorites');
  const favoritedMeLabel = useGoogleTranslate('Favorited Me');

  usePageTitle(pageTitle);
  const [activeTab, setActiveTab] = useState('likes-me');
  const navigate = useNavigate();
  const isFullAccess = useUserStore(state => state.hasFullAccess());
  const isPremium = useUserStore(state => state.isPremium());
  const canFavorite = useUserStore(state => state.canFavorite());

  // Fetch current subscription from API
  const { data: subscriptionData } = useQuery({
    queryKey: ['user-subscription'],
    queryFn: getUserSubscriptionLogs,
  });

  // Check if user is premium based on API subscription data
  const isPremiumFromAPI = () => {
    if (!subscriptionData?.subscription) return false;
    
    const productName = (subscriptionData.subscription.product_name || '').toLowerCase();
    return productName.includes('premium');
  };

  const { data: likesMe, isLoading: isLoadingLikesMe } = useQuery({
    queryKey: ['likes-me'],
    queryFn: getLikesMe,
    enabled: activeTab === 'likes-me',
  });

  const { data: myLikes, isLoading: isLoadingMyLikes } = useQuery({
    queryKey: ['my-likes'],
    queryFn: getMyLikes,
    enabled: activeTab === 'my-likes',
  });

  const { data: viewedMe, isLoading: isLoadingViewedMe } = useQuery({
    queryKey: ['viewed-me'],
    queryFn: getViewedMe,
    enabled: activeTab === 'viewed-me',
  });

  const { data: favoritedMe, isLoading: isLoadingFavoritedMe } = useQuery({
    queryKey: ['favorited-me'],
    queryFn: getFavoritedMe,
    enabled: activeTab === 'favorited-me',
  });

  const { data: myFavorites, isLoading: isLoadingMyFavorites } = useQuery({
    queryKey: ['my-favorites'],
    queryFn: getMyFavorites,
    enabled: activeTab === 'my-favorites',
  });


  const UserImage = ({ image, alt, ...props }) => {
    const [imgError, setImgError] = useState(false);

    const getImageUrl = (image) => {
      if (!image) return null;
      if (image.startsWith('http')) return image;
      return `${import.meta.env.VITE_REACT_APP_BASE_URL || ''}/storage/${image}`;
    };

    if (!image || imgError) {
      return <FaUser size={30} />;
    }

    return (
      <img
        src={getImageUrl(image)}
        alt={alt}
        onError={() => setImgError(true)}
        {...props}
      />
    );
  };

  const getTimeAgo = (date) => {
    const minutes = Math.floor((new Date() - new Date(date)) / 60000);
    if (minutes < 60) return `${minutes} Min Ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} Hours Ago`;
    const days = Math.floor(hours / 24);
    return `${days} Days Ago`;
  };

  const renderUserList = (data, isLoading, isRestricted = false, isFavorites = false) => {
    if (isRestricted) {
      return (
        <div className="text-center py-5">
          <div className="mb-3">
            <span style={{ fontSize: '3rem' }}>🔒</span>
          </div>
          <h4>{upgradeToUnlock}</h4>
          <p className="text-muted mb-4">
            {upgradeText}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/subscription-plans')}
          >
            {viewPlans}
          </button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">{loadingLabel}</p>
        </div>
      );
    }

    // Handle favorites response structure
    // For favorites: data is already an array (returned from service)
    // For other tabs: data has a data property with the array
    const usersList = isFavorites
      ? (Array.isArray(data) ? data : [])
      : (data?.data || []);

    if (!usersList || usersList.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="mt-3">{noUsersFound}</p>
        </div>
      );
    }

    return (
      <div className="likes-list">
        {usersList.map((item) => {
          // For favorites, user data is directly in item
          // For other tabs, user data is in item.target
          const user = isFavorites ? item : item.target;
          // For favorites, use favorite_created_at (when favorited), otherwise use created_at (when the action happened)
          const createdAt = isFavorites
            ? (item.favorite_created_at || item.pivot?.created_at || item.favorited_at || item.created_at)
            : item.created_at;

          return (
            <div key={item.id || user?.id} className="likes-item d-flex align-items-center justify-content-between py-3 px-2">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="user-image-wrapper rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '50px', height: '50px', overflow: 'hidden', backgroundColor: '#f0f0f0', cursor: 'pointer' }}
                  onClick={() => user?.id && navigate(`/user/profile/${user.id}`)}
                >
                  <UserImage
                    image={user?.image}
                    alt={user?.username || user?.full_name}
                    className="rounded-circle"
                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="fw-semibold"
                    style={{ cursor: 'pointer' }}
                    onClick={() => user?.id && navigate(`/user/profile/${user.id}`)}
                  >
                    {user?.username || user?.full_name || 'Unknown User'}
                  </span>
                  <PremiumBadge user={user} variant="small" />
                </div>
              </div>
              {createdAt && (
                <span className="text-muted small">{getTimeAgo(createdAt)}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <InnerBanner heading={pageTitle} />
      <section className="py-sm-5 py-4">
        <Container>
          <Row>
            <Col xs={12}>
              <div className="likes-container site-card">
                <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                  <Nav variant="pills" className="likes-tabs mb-4">
                    <Nav.Item>
                      <Nav.Link eventKey="likes-me">{likesMeLabel}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="my-likes">{myLikesLabel}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="viewed-me">{viewedMeLabel}</Nav.Link>
                    </Nav.Item>
                    {isPremiumFromAPI() && (
                      <>
                        <Nav.Item>
                          <Nav.Link eventKey="my-favorites">{myFavoritesLabel}</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="favorited-me">{favoritedMeLabel}</Nav.Link>
                        </Nav.Item>
                      </>
                    )}
                  </Nav>

                  <Tab.Content>
                    <Tab.Pane eventKey="likes-me">
                      {renderUserList(likesMe, isLoadingLikesMe, !isFullAccess)}
                    </Tab.Pane>

                    <Tab.Pane eventKey="my-likes">
                      {renderUserList(myLikes, isLoadingMyLikes)}
                    </Tab.Pane>

                    <Tab.Pane eventKey="viewed-me">
                      {renderUserList(viewedMe, isLoadingViewedMe, !isFullAccess)}
                    </Tab.Pane>
                    <Tab.Pane eventKey="my-favorites">
                      {renderUserList(myFavorites, isLoadingMyFavorites, !isPremiumFromAPI(), true)}
                    </Tab.Pane>

                    <Tab.Pane eventKey="favorited-me">
                      {renderUserList(favoritedMe, isLoadingFavoritedMe, !isPremiumFromAPI(), true)}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Likes;
